/* eslint-disable no-empty-function */
import {
  type ComponentProps,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import TableFunctions from "@/components/dataTable/TableToolbar";
import Filters from "@/components/filters/Filters";

type ResetFilterCallbacks = {
  handleClearFilter: () => void;
  resetFiltersForTabChange?: () => void;
};

type DataTableFilterProps<
  RowData = unknown,
  FilterParams = unknown,
> = ComponentProps<typeof Filters<RowData, FilterParams>> &
  ResetFilterCallbacks;

type ResetSearchCallbacks = {
  resetSearch: () => void;
};

type ResetColumnOrderCallbacks = {
  hasColumnReordered: boolean;
  resetColumnOrder: () => void;
};

type DataTableFunctionProps = ComponentProps<typeof TableFunctions> &
  ResetSearchCallbacks &
  ResetColumnOrderCallbacks;

type DataTableSlotContextValue<RowData = unknown, FilterParams = unknown> = {
  filters: ReactNode | null;
  functions: ReactNode | null;
  setFilters: (
    filters: DataTableFilterProps<RowData, FilterParams> | null,
  ) => void;
  setFunctions: (functions: DataTableFunctionProps | null) => void;
  resetPagination: (() => void) | undefined;
  resetFilter: (() => void) | undefined;
  resetSorting: (() => void) | undefined;
  resetSearch: (() => void) | undefined;
};

export const DataTableSlotContext = createContext<DataTableSlotContextValue>({
  filters: null,
  functions: null,
  setFilters: () => {},
  setFunctions: () => {},
  resetPagination: () => {},
  resetFilter: () => {},
  resetSorting: () => {},
  resetSearch: () => {},
});

export const useDataTableSlotContext = <
  RowData,
  FilterParams,
>(): DataTableSlotContextValue<RowData, FilterParams> => {
  return useContext(DataTableSlotContext) as DataTableSlotContextValue<
    RowData,
    FilterParams
  >;
};

const DataTableSlotContextProvider = <RowData, FilterParams>({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const [filters, setFilters] = useState<DataTableFilterProps<
    RowData,
    FilterParams
  > | null>(null);
  const [functions, setFunctions] = useState<DataTableFunctionProps | null>(
    null,
  );

  const resetFilter =
    filters?.resetFiltersForTabChange ?? filters?.handleClearFilter;
  const resetPagination = functions?.functions?.pagination?.resetPagination;
  const resetSorting = functions?.functions?.pagination?.resetSorting;
  const resetSearch = functions?.resetSearch;

  const setFiltersStable = useCallback(
    setFilters as (
      filters: DataTableFilterProps<unknown, unknown> | null,
    ) => void,
    [],
  );

  const renderedFilters = useMemo(
    () => (filters ? <Filters {...filters} /> : null),
    [filters],
  );

  const renderedFunctions = useMemo(
    () => (functions ? <TableFunctions {...functions} /> : null),
    [functions],
  );

  const contextValue = useMemo(
    () => ({
      filters: renderedFilters,
      functions: renderedFunctions,
      setFilters: setFiltersStable,
      setFunctions,
      resetPagination,
      resetSorting,
      resetFilter,
      resetSearch,
    }),
    [
      renderedFilters,
      renderedFunctions,
      setFiltersStable,
      setFunctions,
      resetPagination,
      resetSorting,
      resetFilter,
      resetSearch,
    ],
  );

  return (
    <DataTableSlotContext.Provider value={contextValue}>
      {children}
    </DataTableSlotContext.Provider>
  );
};

export default DataTableSlotContextProvider;
